/**
 * Google Picker loader + folder-selection helper.
 *
 * Used with the `drive.file` OAuth scope: the Picker lets a user select an existing
 * Drive folder, which grants the app `drive.file` access to that folder so the server
 * can later write exported documents into it. See `services/integrations/google-drive.ts`
 * (API) for the scope rationale and the `/integration/:uuid/picker-config` endpoint that
 * supplies the OAuth token + developer key consumed here.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface PickedFolder {
  id: string;
  name: string;
}

export interface PickedFile {
  id: string;
  name: string;
  /** Source mimeType reported by the Picker; the server decides export-vs-stream from it. */
  mimeType: string;
}

export interface OpenFolderPickerOptions {
  /** Short-lived OAuth access token (drive.file) for the connected account. */
  accessToken: string;
  /** Browser developer/API key with the Picker API enabled. */
  apiKey: string;
  /** GCP project number that owns the OAuth client (optional but recommended). */
  appId?: string | null;
}

export interface OpenFilePickerOptions extends OpenFolderPickerOptions {
  /** Allow selecting more than one file (Picker MULTISELECT). Default false. */
  multi?: boolean;
  /**
   * Restrict selectable files to these mime types (e.g. the accept list for an asset kind).
   * Omit to allow any file. Google-native Docs/Sheets/Slides are exported server-side, so
   * include their editor mimes when you want them selectable.
   */
  mimeTypes?: string[];
}

declare global {
  interface Window {
    gapi?: any;
    google?: any;
  }
}

const GOOGLE_API_SCRIPT_ID = 'google-api-js';
const GOOGLE_API_SCRIPT_SRC = 'https://apis.google.com/js/api.js';

let pickerApiPromise: Promise<void> | null = null;

/** Inject apis.google.com/js/api.js once and load the `picker` module. Idempotent. */
function loadPickerApi(): Promise<void> {
  if (pickerApiPromise) return pickerApiPromise;

  pickerApiPromise = new Promise<void>((resolve, reject) => {
    const loadPickerModule = () => {
      if (!window.gapi) {
        reject(new Error('Google API script loaded but window.gapi is unavailable'));
        return;
      }
      window.gapi.load('picker', {
        callback: () => resolve(),
        onerror: () => reject(new Error('Failed to load the Google Picker module')),
      });
    };

    const existing = document.getElementById(GOOGLE_API_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.gapi) loadPickerModule();
      else existing.addEventListener('load', loadPickerModule, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_API_SCRIPT_ID;
    script.src = GOOGLE_API_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', loadPickerModule, { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('Failed to load the Google API script')),
      {
        once: true,
      }
    );
    document.body.appendChild(script);
  });

  return pickerApiPromise;
}

/**
 * Open the Google Picker scoped to folders. Resolves with the chosen folder, or null
 * if the user cancels.
 */
export async function openFolderPicker(
  opts: OpenFolderPickerOptions
): Promise<PickedFolder | null> {
  await loadPickerApi();
  const google = window.google;
  if (!google?.picker) {
    throw new Error('Google Picker is unavailable');
  }

  return new Promise<PickedFolder | null>((resolve) => {
    const view = new google.picker.DocsView(google.picker.ViewId.FOLDERS)
      .setSelectFolderEnabled(true)
      .setMimeTypes('application/vnd.google-apps.folder');

    const builder = new google.picker.PickerBuilder()
      .setOAuthToken(opts.accessToken)
      .setDeveloperKey(opts.apiKey)
      .setTitle('Select a destination folder')
      .addView(view)
      .setCallback((data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const doc = data.docs?.[0];
          resolve(doc ? { id: doc.id, name: doc.name } : null);
        } else if (data.action === google.picker.Action.CANCEL) {
          resolve(null);
        }
      });

    if (opts.appId) builder.setAppId(opts.appId);
    builder.build().setVisible(true);
  });
}

/**
 * Open the Google Picker scoped to files (single or multi-select). Resolves with the chosen
 * files, or [] if the user cancels. Under the `drive.file` scope, selecting a file here grants
 * the app durable access to exactly that file - the only `drive.file`-compatible way to read a
 * pre-existing Drive file - so this is the grant step the server's downloadFileForIngest relies
 * on. The persistent UI (the selected-file list) lives in our own components; this iframe is a
 * transient grant modal.
 */
export async function openFilePicker(opts: OpenFilePickerOptions): Promise<PickedFile[]> {
  await loadPickerApi();
  const google = window.google;
  if (!google?.picker) {
    throw new Error('Google Picker is unavailable');
  }

  const FOLDER_MIME = 'application/vnd.google-apps.folder';

  return new Promise<PickedFile[]>((resolve) => {
    // Show folders so the user can browse their Drive hierarchy (not just a flat all-files list).
    // setSelectFolderEnabled stays false so a folder click navigates into it rather than selecting
    // it as a result; only files come back from the picker.
    const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);
    if (opts.mimeTypes?.length) {
      // Keep the folder mime in the filter alongside the accepted file types, otherwise the
      // mimeType filter hides folders and navigation breaks.
      view.setMimeTypes([...opts.mimeTypes, FOLDER_MIME].join(','));
    }

    const builder = new google.picker.PickerBuilder()
      .setOAuthToken(opts.accessToken)
      .setDeveloperKey(opts.apiKey)
      .setTitle('Select files')
      .addView(view)
      .setCallback((data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const docs = (data.docs ?? []) as { id: string; name: string; mimeType: string }[];
          resolve(docs.map((doc) => ({ id: doc.id, name: doc.name, mimeType: doc.mimeType })));
        } else if (data.action === google.picker.Action.CANCEL) {
          resolve([]);
        }
      });

    if (opts.multi) {
      builder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }
    if (opts.appId) builder.setAppId(opts.appId);
    builder.build().setVisible(true);
  });
}
